import React from "react";
import type Stripe from "stripe";
import styled from "styled-components";
import { Trans } from "react-i18next";
import { Button } from "js/Components/Button";
import { Icon } from "js/Components/Icon";
import { Input } from "js/Components/Input";
import FetchingClickShield from "js/react/components/FetchingClickShield";
import { themeColors } from "js/react/sharedStyles";

import { IPromoFormProps, usePromoFormLogic } from "./usePromoFormLogic";
import { t } from "js/i18n";

const PromoContainer = styled.div`
    width: 100%;
    position: relative;
    display: flex;
    flex-direction: column;
    margin-top: 10px;
`;

const PromoInputContainer = styled.div`
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
`;

const PromoInput = styled(Input)`
    width: 100%;
`;

const PromoTerms = styled.div`
    font-size: 14px;
    margin-top: 5px;
    color: #333;
    font-weight: 600;

    > a {
        text-decoration: none;
        color: ${themeColors.ui_blue};
    }
`;

const PromoError = styled.div`
    font-size: 14px;
    margin-top: 5px;
    color: ${themeColors.warning};
    font-weight: 600;
`;

const StyledPromoCoupon = styled.div`
    display: flex;
    flex-flow: row;
    gap: 10px;
    font-size: 15px;
    text-transform: uppercase;
    font-weight: 900;
    color: #333;

    .bai-icon {
        font-size: 22.5px;
        margin-top: -1px;
    }
`;

export function PromoForm(props: IPromoFormProps) {
    const {
        isAddingPromo,
        isApplyingPromo,
        promoError,
        promoCode,
        handleAddPromo,
        handleChangePromoCode,
        handleApplyPromo
    } = usePromoFormLogic(props.verifyPromoCode, props.initialPromotionCode, props.prefillOnly);

    if (!isAddingPromo) {
        return (
            <PromoContainer data-test-id="promo-container">
                <Button unframed large blue onClick={handleAddPromo}>{t("add promo code")}</Button>
            </PromoContainer>
        );
    }

    return (
        <PromoContainer data-test-id="promo-container">
            <FetchingClickShield visible={isApplyingPromo} backgroundColor="rgba(255,255,255,0.7)" />

            <PromoInputContainer>
                <PromoInput width="100%" value={promoCode} onChange={handleChangePromoCode} placeholder={t("Promo Code")} />
                <Button unframed large blue onClick={handleApplyPromo}
                    disabled={!promoCode || promoError || isApplyingPromo}>{t("apply")}</Button>
            </PromoInputContainer>
            {!promoError && <PromoTerms>
                <Trans
                    i18nKey="By clicking apply you agree to the <promoTermsLink>Promotion Terms and Conditions</promoTermsLink>"
                    components={{ promoTermsLink: <a href="/promotion-terms" target="_blank" /> }}
                />
            </PromoTerms>}
            {promoError && <PromoError>{promoError}</PromoError>}
        </PromoContainer>
    );
}

export function PromoCoupon({ coupon }: { coupon: Stripe.Coupon }) {
    const durationText = coupon.duration === "repeating"
        ? t("for {{months}} months", { months: coupon.duration_in_months })
        : t(coupon.duration === "once" ? "once" : "forever");
    const amountText = coupon.amount_off ? `$${coupon.amount_off / 100}` : `${coupon.percent_off}%`;

    return (
        <PromoContainer data-test-id="promo-container">
            <StyledPromoCoupon>
                <Icon color="rgb(143, 177, 48)">confirmation_number</Icon>
                {coupon.name}{" "}
                ({t("{{amount}} off {{duration}}", { amount: amountText, duration: durationText })})
            </StyledPromoCoupon>
        </PromoContainer>
    );
}
